/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { useNotificationContext } from "../../../contexts/notificationContext";
import api from "../../../api";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { configurationMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/configurationMutationKeys";
import { resetPageAfterDeletion } from '../../../utils/resetPageAfterDeletion'
import {
  IntegrationContainer,
  StyledHelperText,
  StyledPasswordField,
} from "./styles";
import {
  StyledAddButton,
  StyledBreadCrumbContainer,
  StyledButtonContainer,
  StyledCancelButton,
  StyledCreateButton,
  StyledCreateTitle,
  StyledFormContainer,
  StyledFormFieldText,
  StyledLink,
  StyledTextField,
  StyledTitle,
  StyledTitleContainer,
} from "./styles";
import {
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import camelize from "../../../utils/camelize";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { LoadingSpinner } from "../../../components/loading_spinner";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  encryptToastCredentialValue,
  MASKED_SENTINEL_PATTERN,
} from "../../../utils/toastCredentialRequestCrypto";
import { ConfirmationDialog } from "../../../components/confirmation_dialog";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../../utils/checkAdmin";
import { formatDate } from "../../../utils/formatDate.js"
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Query_Keys } from "../../../lib/queryKeys.js";

export const Integrations = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [fetchingLoader, setFetchingLoader] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [configuration, setConfiguration] = useState({ name: "", key: "" });
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState({});
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editConfiguration, setEditConfiguration] = useState({});
  const [keyClearedOnFocus, setKeyClearedOnFocus] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const queryClient = useQueryClient();

  const showNotication = useNotificationContext();

  const fetchConfiguration = async () => {
    const response = await api.get("/v1/admin/configurations",{
      params: {
        per_page: itemsPerPage,
        page: page,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      },
    })

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: configurationData, isFetching } = useQuery({
    queryKey: Query_Keys.configuration({
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchConfiguration,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const drawerWidth = 470;

  const TableColumns = [
    { key: "integration", sortable: true },
    { key: "last_edited_by", sortable: true },
    { key: "last_edited", sortable: true },
    { key: "actions", sortable: false },
  ];

  const columns = TableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        integration: record?.name,
        name: record?.name,
        last_edited_by: record?.updated_by ? record?.updated_by : "-",
        last_edited: formatDate(record?.updated_at, record?.time_zone_abbr),
        actions: (
          <Box>
            <Tooltip title="Edit" arrow onClick={() => handleEdit(record)}>
              <span>
                <IconButton
                  data-testid="integration-edit-button"
                  disabled={!(superAdmin?.role === 'admin')}
                >
                  <ModeEditIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#707070',
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete" arrow onClick={() => handleDelete(record)}>
              <span>
                <IconButton
                  data-testid="integration-delete-button"
                  disabled={!(superAdmin?.role === 'admin')}
                >
                  <DeleteIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#707070',
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      };
    });
  };

  const rows = TableRows(configurationData?.data);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleDrawerOpen = () => {
    setShowKey(false);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setConfiguration({ name: "", key: "" });
    setShowKey(false);
    setErrors({});
  };

  const handleClickShowKey = () => setShowKey((show) => !show);

  const handleName = (event) => {
    if (errors?.name) {
      setErrors({ ...errors, name: "" });
    }

    setConfiguration({ ...configuration, name: event.target.value });
  };

  const handleKey = (event) => {
    let value = event.target.value;
    if (value?.[value?.length - 1] === " ") return;

    if (errors?.key) {
      setErrors({ ...errors, key: "" });
    }

    setConfiguration({ ...configuration, key: event.target.value });
  };

  const { mutateAsync: createConfigMutation } = useOtMutation({
    mutationFn: async (data) => await api.post("/v1/admin/configurations", { configuration: data }),
    invalidateKeys: configurationMutationKeyGroups.create,
    onSuccess: () => {
      showNotication("Integration has been created successfully!", "success");
      setDrawerOpen(false);
      setConfiguration({ name: "", key: "" });
      setErrors({});
      setShowKey(false);
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setDisabled(false)
  });

  const handleAdd = async () => {
    setDisabled(true);

    const formattedConfiguration = {
      ...configuration,
      key: encryptToastCredentialValue(configuration.key),
    };

    await createConfigMutation(formattedConfiguration);
  };

  const handleEdit = async (configuration) => {
    setEditConfiguration({});
    setKeyClearedOnFocus(false);
    setShowKey(false);
    setEditErrors({});
    setEditDrawerOpen(true);

    // The API serves a mask, never the stored ciphertext, so there is nothing to decrypt.
    const data = await getConfiguration(configuration.id);

    setEditConfiguration(data);
  };

  const getConfiguration = async (id) => {
    const data = await api
      .get(`/v1/admin/configurations/${id}`)
      .then((response) => response.data);

    return data;
  };

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false);
    setShowKey(false);
    setEditConfiguration({});
    setKeyClearedOnFocus(false);
    setEditErrors({});
  };

  const handleUpdateChange = (value, field) => {
    if (field === "key" && value?.[value?.length - 1] === " ") return;

    setEditConfiguration({ ...editConfiguration, [field]: value });
    if (editErrors[field] !== "") setEditErrors({ ...editErrors, [field]: "" });
  };

  // A password field renders the mask as dots, so clear it and make "type to
  // replace" the interaction.
  const handleKeyFocus = () => {
    if (MASKED_SENTINEL_PATTERN.test(editConfiguration?.key || "")) {
      setEditConfiguration((previous) => ({ ...previous, key: "" }));
      setKeyClearedOnFocus(true);
    }
  };

  const { mutateAsync: updateConfigMutation } = useOtMutation({
    mutationFn: async ({ id, data }) => await api.put(`/v1/admin/configurations/${id}`, data),
    invalidateKeys: configurationMutationKeyGroups.update,
    onSuccess: () => {
      setEditDrawerOpen(false);
      setEditErrors({});
      showNotication("Integration has been updated successfully!", "success");
      setShowKey(false);
    },
    onError: (error) => {
      setEditErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setDisabled(false)
  });

  const handleUpdate = async () => {
    setDisabled(true);

    // Only the writable fields. An untouched mask passes through
    // encryptToastCredentialValue unchanged and the backend skips it.
    const data = { name: editConfiguration?.name };

    // Omit rather than send "": clear-on-focus would otherwise turn tabbing past
    // the field into an explicit blank, which the interaction rejects.
    if (editConfiguration?.key) {
      data.key = encryptToastCredentialValue(editConfiguration.key);
    }

    await updateConfigMutation({ id: editConfiguration?.id, data });
  };

  const handleDelete = (configuration) => {
    setSelectedConfiguration(configuration);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedConfiguration({});
  };

  const { mutateAsync: deleteConfigMutation } = useOtMutation({
    mutationFn: async (id) => await api.delete(`/v1/admin/configurations/${id}`),
    invalidateKeys: configurationMutationKeyGroups.delete,
    onSuccess: (res) => {
      showNotication("Integration has been deleted successfully!", "success");
      setSelectedConfiguration({});

      resetPageAfterDeletion({ totalCount: configurationData?.pageCount, removed: 1, page, itemsPerPage, setPage })

      if (res?.data?.id === editConfiguration?.id && editDrawerOpen) {
        handleEditDrawerClose();
      }
    },
    onError: (error) => {
      console.log(error);
    },
    onSettled: () => setFetchingLoader(false)
  });

  const handleDialogConfirm = async () => {
    setDialogOpen(false);
    setFetchingLoader(true);
    await deleteConfigMutation(selectedConfiguration.id);
  };

  const dialogBody = () => {
    return (
      <>
        This action will permanently delete
        <span style={{ fontWeight: "600" }}>
          {` '${selectedConfiguration?.name}'`}
        </span>
      </>
    );
  };

  return (
    <IntegrationContainer
      drawerOpen={drawerOpen || editDrawerOpen}
      drawerWidth={drawerWidth}
    >
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Configuration</StyledLink>
          <StyledLink color={"#515152"}>Integrations</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <StyledTitleContainer>
        <StyledTitle>My Integrations</StyledTitle>
      </StyledTitleContainer>

      <Divider style={{ borderStyle: "solid", borderWidth: "1px" }} />

      <Box style={{ padding: "25px 0px" }}>
        <StyledCreateButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleDrawerOpen}
          disabled={!(superAdmin?.role === 'admin')}
        >
          Create Integration
        </StyledCreateButton>
      </Box>

      {isFetching ? (
        <Loader />
      ) : ( !((superAdmin?.role === 'admin' || superAdmin?.role === 'viewer')) ? (
            <div></div>
          ) : (
              <IndexTable
                report={false}
                columns={columns}
                data={rows}
                tableId="integrations"
                type="entries"
                sortMode="server"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                page={page}
                setPage={setPage}
                pageCount={configurationData?.pageCount}
              />
            )
      )}

      {/* Create Integration Drawer*/}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            marginTop: "calc(35px * 2)",
          },
        }}
        variant="persistent"
        anchor="right"
        open={drawerOpen}
      >
        <div style={{ padding: "0px 15px" }}>
          <StyledCreateTitle>Create New Integration</StyledCreateTitle>

          <StyledFormContainer>
            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Integration name<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={configuration?.name}
                onChange={handleName}
                error={errors?.name}
                helperText={errors?.name}
                autoComplete="off"
              />
            </div>
            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                API Key<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledPasswordField
                fullWidth
                size="small"
                type={showKey ? "text" : "password"}
                value={configuration?.key}
                onChange={handleKey}
                error={errors?.key}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowKey}>
                      {showKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                autoComplete="new-password"
              />
              <StyledHelperText>
                {!!errors?.key && `${errors?.key}`}
              </StyledHelperText>
            </div>
          </StyledFormContainer>
        </div>

        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            width: drawerWidth,
            height: "70px",
          }}
          elevation={2}
        >
          <StyledButtonContainer>
            <StyledCancelButton onClick={handleDrawerClose} variant="outlined">
              Cancel
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleAdd}
              variant="contained"
              disabled={disabled}
            >
              {disabled ? <LoadingSpinner isLoading={true} /> : "Add"}
            </StyledAddButton>
          </StyledButtonContainer>
        </Paper>
      </Drawer>

      {/* Edit Integration Drawer*/}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            marginTop: "calc(35px * 2)",
          },
        }}
        variant="persistent"
        anchor="right"
        open={editDrawerOpen}
      >
        <div style={{ padding: "0px 15px" }}>
          <StyledCreateTitle>Update Integration</StyledCreateTitle>

          {Object.keys(editConfiguration)?.length > 0 ? (
            <StyledFormContainer>
              <div style={{ marginTop: "3%" }}>
                <StyledFormFieldText>Integration name</StyledFormFieldText>
                <StyledTextField
                  fullWidth
                  size="small"
                  value={editConfiguration?.name}
                  onChange={(event) =>
                    handleUpdateChange(event.target.value, "name")
                  }
                  error={editErrors?.name}
                  helperText={editErrors?.name}
                  autoComplete="off"
                />
              </div>
              <div style={{ marginTop: "3%" }}>
                <StyledFormFieldText>API Key</StyledFormFieldText>
                <StyledPasswordField
                  fullWidth
                  size="small"
                  type={showKey ? "text" : "password"}
                  value={editConfiguration?.key}
                  onChange={(event) =>
                    handleUpdateChange(event.target.value, "key")
                  }
                  onFocus={handleKeyFocus}
                  error={editErrors?.key}
                  inputProps={{ "data-testid": "integration-edit-key-field" }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowKey}>
                        {showKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  autoComplete="new-password"
                />
                <StyledHelperText>
                  {editErrors?.key
                    ? `${editErrors?.key}`
                    : MASKED_SENTINEL_PATTERN.test(editConfiguration?.key || "")
                      ? "A key is saved. Type a new key to replace it."
                      : keyClearedOnFocus && !editConfiguration?.key
                        ? "The saved key is unchanged unless you type a new one."
                        : ""}
                </StyledHelperText>
              </div>
            </StyledFormContainer>
          ) : (
            <Box sx={{ position: "absolute", left: "45%", top: "40%" }}>
              <Loader />
            </Box>
          )}
        </div>

        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            width: drawerWidth,
            height: "70px",
          }}
          elevation={2}
        >
          <StyledButtonContainer>
            <StyledCancelButton
              onClick={handleEditDrawerClose}
              variant="outlined"
            >
              Cancel
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleUpdate}
              variant="contained"
              disabled={disabled}
            >
              {disabled ? <LoadingSpinner isLoading={true} /> : "Update"}
            </StyledAddButton>
          </StyledButtonContainer>
        </Paper>
      </Drawer>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => handleDialogClose()}
        Icon={CheckIcon}
        dialogTitle="Are you sure you want to delete the integration?"
        dialogBody={dialogBody()}
        confirmationText="Yes, I want to delete the integration"
        handleConfirm={handleDialogConfirm}
        isLoading={fetchingLoader}
      />
    </IntegrationContainer>
  );
};
